/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { PlaybackContentType } from '../playback/enums';
import { ITivoSubscription, TivoSubscriptionModel } from '../tivo';
import { TOfferPreviewCustomData, TPlayNextInfo, TVodOfferPreviewCustomData } from '../types';
import { PlayNextState } from './enums';
import { IPlayable, IPlayNextManager } from './interfaces';

export class PlayNextManager implements IPlayNextManager {
    private static readonly THIRTY_SECONDS: number = 30;

    private static mTimer: ReturnType<typeof setTimeout> | undefined;

    private mIsPlayNextMonitoring: boolean = false;

    private mPlayNextState: PlayNextState = PlayNextState.NONE;

    private readonly mPlayNextSubscription: ITivoSubscription<PlayNextState>;

    public constructor() {
        this.mPlayNextSubscription = new TivoSubscriptionModel<PlayNextState>();
    }

    // eslint-disable-next-line class-methods-use-this
    private calculateTime(
        offerPreview: TOfferPreviewCustomData,
        vodOfferPreview: TVodOfferPreviewCustomData,
        playerCurrentTime: number,
        duration: number,
    ): number {
        const contentDuration =
            duration ??
            offerPreview?.contentPreview?.duration ??
            vodOfferPreview?.contentPreview?.duration ??
            0;

        const playNextTimerEndTime = contentDuration - playerCurrentTime;
        return Math.max(0, playNextTimerEndTime);
    }

    // eslint-disable-next-line class-methods-use-this
    private hasNextEpisodeAvailable(playNextData: TPlayNextInfo): boolean {
        return !!playNextData?.playNextInfo?.next;
    }

    private notifyPlayNextStateChange(state: PlayNextState): void {
        this.mPlayNextState = state;
        this.mPlayNextSubscription.notify(state);
    }

    private setPlayNextTimer(time: number, notifyState: PlayNextState): void {
        this.clearTimer();
        PlayNextManager.mTimer = setTimeout(() => {
            this.notifyPlayNextStateChange(notifyState);
        }, time * MILLISECONDS_IN_A_SECOND);
    }

    // eslint-disable-next-line class-methods-use-this
    private clearTimer(): void {
        if (PlayNextManager.mTimer !== undefined) {
            clearTimeout(PlayNextManager.mTimer);
            PlayNextManager.mTimer = undefined;
        }
    }

    public checkIsPlayNextEnabled(playable: IPlayable, playNextData: TPlayNextInfo): boolean {
        if (playable.playbackContentType === PlaybackContentType.LIVE) {
            return false;
        }
        return this.hasNextEpisodeAvailable(playNextData);
    }

    public clearPlayNextBannerTimer(): void {
        this.clearTimer();
        this.notifyPlayNextStateChange(PlayNextState.PLAY_NEXT_BANNER_ENDED);
    }

    public clearPlayNextWhisperTimer(): void {
        this.clearTimer();
        this.notifyPlayNextStateChange(PlayNextState.PLAY_NEXT_WHISPER_ENDED);
    }

    public getPlayNextState(): PlayNextState {
        return this.mPlayNextState;
    }

    public getPlayNextStateChangeListener(): ITivoSubscription<PlayNextState> {
        return this.mPlayNextSubscription;
    }

    public monitorPlayNextTime(
        playable: IPlayable,
        playNextData: TPlayNextInfo,
        offerPreview: TOfferPreviewCustomData,
        vodOfferPreview: TVodOfferPreviewCustomData,
        playerCurrentTime: number,
        duration: number,
    ): void {
        if (!this.checkIsPlayNextEnabled(playable, playNextData)) {
            return;
        }

        const playNextTimerEndTime = this.calculateTime(
            offerPreview,
            vodOfferPreview,
            playerCurrentTime,
            duration,
        );

        if (playNextTimerEndTime <= PlayNextManager.THIRTY_SECONDS) {
            if (this.mIsPlayNextMonitoring) {
                this.notifyPlayNextStateChange(PlayNextState.PLAY_NEXT_TIMER_ENDED);
                this.mIsPlayNextMonitoring = false;
            }
        } else {
            this.mIsPlayNextMonitoring = true;
        }
    }

    public resetPlayNextStates(): void {
        this.mIsPlayNextMonitoring = false;
        this.clearTimer();
        this.notifyPlayNextStateChange(PlayNextState.NONE);
    }

    public startPlayNextBannerTime(isAutoPlayEnabled: boolean): void {
        if (isAutoPlayEnabled) {
            this.setPlayNextTimer(
                PlayNextManager.THIRTY_SECONDS,
                PlayNextState.PLAY_NEXT_BANNER_ENDED,
            );
        }
    }

    public startPlayNextWhisperTime(): void {
        this.setPlayNextTimer(
            PlayNextManager.THIRTY_SECONDS,
            PlayNextState.PLAY_NEXT_WHISPER_ENDED,
        );
    }
}
